import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { laddaNerBilaga } from './forslagApi';
import { IkonFil, Statusetikett } from './MinaArendenLista';
import { STEG, datumTid, stegIndex, visning } from './minaArendenStatus';

/**
 * Ett eget ärende i förslagspanelen (BL-377, mina-forslag-komponentspec.md avsnitt 1–5):
 * läget i klartext, stegen, kommentaren, händelserna med mejlen och det användaren skrev.
 * Kommentaren och texterna visas som text, aldrig som HTML.
 */

const HANDELSE = { mottagen: 'Mottagen', registrerad: 'Registrerad', dubblett: 'Kopplad till', avvisad: 'Avvisad', besvarad: 'Besvarad', paborjad: 'Arbetet har börjat', levererad: 'Levererad' };

function IkonKuvert() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="3.5" width="12" height="9" rx="1.5" /><path d="M2.5 4.5 8 9l5.5-4.5" />
    </svg>
  );
}

function IkonOppna() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 2.5h4.5V7" /><path d="M13.5 2.5 7.5 8.5" /><path d="M11.5 9.5v3a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h3" />
    </svg>
  );
}

function IkonLaddaNer() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.5 11v2h11v-2" /><path d="M8 2.5v7.5" /><path d="M5 7.5L8 10.5l3-3" />
    </svg>
  );
}

function storlek(b) {
  if (!b && b !== 0) return '';
  return b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} kB` : `${(b / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}

function handelseText(h) {
  if (h.typ === 'registrerad' && h.bl) return `Registrerad som ${h.bl}`;
  if (h.typ === 'dubblett' && h.bl) return `Kopplad till ${h.bl}`;
  return HANDELSE[h.typ] || h.typ;
}

/**
 * @param {{ arende: object|null, fel: string, onForsok: () => void, onStang: () => Promise<boolean> }} props
 *   onStang frågar om ett osparat utkast och stänger panelen; false = användaren avbröt.
 */
export default function MinaArendenDetalj({ arende, fel, onForsok, onStang }) {
  const [nerfel, setNerfel] = useState('');
  const navigate = useNavigate();

  if (fel) {
    return (
      <div className="mf-tomt" role="alert">
        <p className="mf-tomt__titel">Det gick inte att hämta ärendet</p>
        <p className="mf-tomt__text">{fel}</p>
        <button type="button" className="forslag-btn forslag-btn--sek" onClick={onForsok}>Försök igen</button>
      </div>
    );
  }
  if (!arende) return <div className="mf-skel__ruta mf-skel__ruta--block" aria-hidden="true" />;

  const v = visning(arende);
  const steg = stegIndex(arende);
  const arFraga = arende.typ === 'question';
  const kommandeLeverans = steg !== null && steg < 4;

  const laddaNer = async (filnamn) => {
    setNerfel('');
    try { await laddaNerBilaga(arende.ref, filnamn); } catch (e) { setNerfel(e.message); }
  };

  return (
    <>
      <section className="mf-sektion mf-lage">
        <div className="mf-lage__rad">
          <Statusetikett rad={arende} />
          {arende.bl && !arende.dubblett && <span className="mf-bl">{arende.bl}</span>}
        </div>
        {v.text && <p className="mf-lage__text">{v.text}</p>}
        {arende.tid && <p className="mf-lage__tid">Beräknad hantering: <strong>{arende.tid}</strong></p>}
        {arende.pa_gang_lank && (
          <div>
            <Link
              className="forslag-btn forslag-btn--sek forslag-btn--sm"
              to={`/pa-gang?post=${encodeURIComponent(arende.bl)}`}
              onClick={async (e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;   // ny flik: panelen står kvar
                e.preventDefault();
                if (await onStang()) navigate(`/pa-gang?post=${encodeURIComponent(arende.bl)}`);
              }}
            >
              {arende.dubblett ? `Se ${arende.bl} i På gång` : 'Se i På gång'}<IkonOppna />
            </Link>
          </div>
        )}
        {arende.kommentar && (
          <div className="mf-kommentar">
            <p className="mf-kommentar__vem">
              {arFraga ? 'Svar från systemansvarig' : 'Kommentar från systemansvarig'}
              {arende.kommentar.tid ? ` · ${datumTid(arende.kommentar.tid).replace(/ \d\d:\d\d$/, '')}` : ''}
            </p>
            <p className="mf-kommentar__text">{arende.kommentar.text}</p>
          </div>
        )}
        {steg !== null && (
          <ol className="mf-steg" aria-label="Steg">
            {STEG.map((s, i) => (
              <li key={s} className={i < steg ? 'is-klar' : i === steg ? 'is-nu' : undefined} aria-current={i === steg ? 'step' : undefined}>
                <span className="mf-steg__prick" />{s}
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="mf-sektion">
        <h3 className="mf-sektion__rubrik">Händelser</h3>
        <ol className="mf-handelser">
          {(arende.handelser || []).map((h) => (
            <li className="mf-handelse" key={`${h.typ}-${h.tid || ''}`}>
              <span className="mf-handelse__ikon"><IkonKuvert /></span>
              <span className="mf-handelse__text">
                {handelseText(h)}
                <span className="mf-handelse__sub">
                  {[datumTid(h.tid), h.mejlat ? 'mejl skickat till dig' : null].filter(Boolean).join(' · ') || ' '}
                </span>
              </span>
            </li>
          ))}
          {kommandeLeverans && (
            <li className="mf-handelse mf-handelse--kommande">
              <span className="mf-handelse__ikon"><IkonKuvert /></span>
              <span className="mf-handelse__text">Levererad<span className="mf-handelse__sub">Du får ett mejl när ärendet är i drift</span></span>
            </li>
          )}
        </ol>
      </section>

      <section className="mf-sektion">
        <h3 className="mf-sektion__rubrik">Det du skrev</h3>
        {arende.beskrivning && <p className="mf-skrev__text">{arende.beskrivning}</p>}
        {arende.motivering && <p className="mf-skrev__text">{arende.motivering}</p>}
        {arende.forvantat && <p className="mf-skrev__text">{arende.forvantat}</p>}
        {!arende.beskrivning && !arende.motivering && !arende.forvantat && <p className="mf-skrev__text mf-skrev__text--tom">Ingen beskrivning.</p>}
        {(arende.bilagor || []).map((b) => (
          <div className="forslag-fil" key={b.filnamn}>
            <span className="forslag-fil__ikon"><IkonFil /></span>
            <span className="forslag-fil__namn">{b.filnamn}</span>
            <span className="forslag-fil__storlek">{storlek(b.storlek)}</span>
            <button type="button" className="kp-icon-btn" aria-label={`Ladda ner ${b.filnamn}`} onClick={() => laddaNer(b.filnamn)}><IkonLaddaNer /></button>
          </div>
        ))}
        {nerfel && <p className="forslag-fel" role="alert">{nerfel}</p>}
      </section>
    </>
  );
}
